import { useRef } from "react";
import { Download, FileText, FileUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/context/ToastContext";
import { MAX_PDF_MB } from "@/components/proposals/UploadDocumentDialog";

/** The PDF of an uploaded proposal or quote, shown in the page, with a way to replace it while it can still be edited */
export function UploadedPdfViewer({
  src,
  fileName,
  fileSize,
  canReplace,
  busy,
  onReplace,
  hint,
}: {
  src: string;
  fileName?: string | null;
  fileSize?: number | null;
  canReplace: boolean;
  busy?: boolean;
  onReplace: (file: File) => void;
  hint?: string;
}) {
  const { error } = useToast();
  const ref = useRef<HTMLInputElement>(null);
  const choose = (f: File | null) => {
    if (!f) return;
    if (!/\.pdf$/i.test(f.name)) return error("PDF only", "Choose a PDF file");
    if (f.size > MAX_PDF_MB * 1024 * 1024) return error("File too large", `The PDF must be under ${MAX_PDF_MB} MB`);
    onReplace(f);
  };
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="size-4 text-primary shrink-0" />
          <span className="truncate text-sm font-semibold">{fileName || "document.pdf"}</span>
          {!!fileSize && <span className="text-xs text-muted-foreground shrink-0">{(fileSize / 1024 / 1024).toFixed(1)} MB</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={() => window.open(src, "_blank")}>
            <Download className="size-3.5" /> Open
          </Button>
          {canReplace && (
            <>
              <input ref={ref} type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => { choose(e.target.files?.[0] || null); e.target.value = ""; }} />
              <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5 text-xs" disabled={busy} onClick={() => ref.current?.click()}>
                <FileUp className="size-3.5" /> Replace PDF
              </Button>
            </>
          )}
        </div>
      </div>
      {hint && <p className="px-4 pt-2 text-[11px] text-muted-foreground">{hint}</p>}
      <iframe key={src + (fileName || "")} title="Uploaded PDF" src={`${src}#toolbar=0`} className="block w-full h-[70vh] bg-muted/40" />
    </div>
  );
}
